/**
 * src/components/ui/ReceiptUploadField.tsx
 * Reusable receipt image uploader with Cloudflare R2 edge upload and instant preview
 */

import React, { useState, useRef } from 'react';
import { UploadCloud, Image, X, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import { api } from '../../services/api';
import { parseReceiptImage, type ParsedReceiptData } from '../../utils/receiptParser';

interface ReceiptUploadFieldProps {
  receiptUrl: string | null;
  onChange: (url: string | null) => void;
  onParsed?: (data: ParsedReceiptData) => void;
  disabled?: boolean;
}

export const ReceiptUploadField: React.FC<ReceiptUploadFieldProps> = ({
  receiptUrl,
  onChange,
  onParsed,
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [parsedBanner, setParsedBanner] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Only image files (PNG, JPG, WebP) are supported.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size exceeds 5MB limit.');
      return;
    }

    setUploadError('');
    setIsUploading(true);
    setParsedBanner(null);

    try {
      // 1. Run smart receipt auto-fill parser
      if (onParsed) {
        const parsed = await parseReceiptImage(file);
        onParsed(parsed);
        if (parsed.amount) {
          setParsedBanner(`Auto-detected ₹${parsed.amount} · ${parsed.suggestedCategory}`);
        }
      }

      // 2. Upload to Cloudflare R2
      const res = await api.uploads.receipt(file);
      if (res && res.receiptUrl) {
        onChange(res.receiptUrl);
      } else {
        throw new Error('Upload succeeded but no URL was returned');
      }
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload receipt');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setUploadError('');
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        RECEIPT ATTACHMENT (OPTIONAL)
      </label>

      {receiptUrl ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <img
              src={receiptUrl}
              alt="Receipt preview"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '4px',
                objectFit: 'cover',
                border: '1px solid var(--border-glass)'
              }}
            />
            <div style={{ fontSize: '0.8125rem' }}>
              <div style={{ color: '#34d399', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckCircle2 size={13} />
                <span>Receipt attached</span>
              </div>
              <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                Stored securely in Vault
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
            title="Remove attachment"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={disabled || isUploading}
          />

          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.625rem',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-md)',
              border: '1px dashed var(--border-glass)',
              background: 'rgba(255, 255, 255, 0.02)',
              cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              color: 'var(--text-secondary)'
            }}
          >
            {isUploading ? (
              <>
                <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-cyan)' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--accent-cyan)' }}>Uploading receipt securely...</span>
              </>
            ) : (
              <>
                <UploadCloud size={16} color="var(--accent-cyan)" />
                <span style={{ fontSize: '0.8125rem' }}>Click to attach bill / receipt photo (PNG, JPG)</span>
              </>
            )}
          </div>
        </div>
      )}

      {parsedBanner && (
        <div style={{
          marginTop: '6px',
          padding: '6px 12px',
          borderRadius: 'var(--radius-pill)',
          background: 'rgba(10, 132, 255, 0.12)',
          border: '1px solid rgba(10, 132, 255, 0.25)',
          color: 'var(--ios-blue)',
          fontSize: '0.75rem',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <Sparkles size={13} />
          <span>{parsedBanner}</span>
        </div>
      )}

      {uploadError && (
        <div style={{ fontSize: '0.75rem', color: '#f87171', marginTop: '4px' }}>
          {uploadError}
        </div>
      )}
    </div>
  );
};
